import { useState } from 'react';
import { Download, Share, Plus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

/**
 * Botón "Instalar app" para el footer del sidebar.
 *
 * - Chrome/Edge/Android (`installable`): instala en 1 clic vía el prompt nativo.
 * - iOS Safari (`ios`): abre un diálogo con la guía manual (Safari no expone
 *   beforeinstallprompt).
 * - Ya instalada / sin soporte: no renderiza nada.
 */
export function InstallAppButton() {
  const { state, promptInstall } = useInstallPrompt();
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  if (state === 'installed' || state === 'unavailable') return null;

  const handleClick = () => {
    if (state === 'ios') {
      setIosGuideOpen(true);
      return;
    }
    void promptInstall();
  };

  return (
    <>
      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleClick}
              tooltip={{ children: <p className="font-medium">Instalar app</p> }}
              className="text-fg-muted hover:bg-surface-hi/55"
            >
              <Download className="shrink-0" />
              <span>Instalar app</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <Dialog open={iosGuideOpen} onOpenChange={setIosGuideOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Instalar en tu iPhone o iPad</DialogTitle>
            <DialogDescription>
              Safari no permite instalar con un botón. Seguí estos pasos para
              tener DenDev Core como app:
            </DialogDescription>
          </DialogHeader>
          <ol className="text-foreground/90 space-y-3 text-body-lg">
            <li className="flex items-center gap-3">
              <span className="bg-surface-hi text-fg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-caption font-semibold">
                1
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                Tocá el botón
                <Share className="text-primary inline h-4 w-4" aria-label="Compartir" />
                <span className="font-medium">Compartir</span> en la barra de Safari.
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="bg-surface-hi text-fg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-caption font-semibold">
                2
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                Elegí
                <Plus className="text-primary inline h-4 w-4" aria-label="Agregar" />
                <span className="font-medium">Agregar a inicio</span>.
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="bg-surface-hi text-fg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-caption font-semibold">
                3
              </span>
              <span>
                Confirmá con <span className="font-medium">Agregar</span>. Listo:
                abrila desde el ícono de la pantalla de inicio.
              </span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
